/**
 * SelectionSystem.ts
 * Manages the player's unit selection via single click and drag box-select.
 *
 * Consumers:
 *   - Game.ts fires selectAt() on left-click and boxSelect() on drag-release.
 *   - SelectionPanel reads the `selected` set to display unit info.
 */

import { Faction } from '../types';
import type { Unit } from '../entities/Unit';
import type { Camera } from '../map/Camera';
import type { TileMap } from '../map/TileMap';
import type { WorldPosition } from '../types';
import { UNIT_HIT_RADIUS } from '../constants';

export class SelectionSystem {
  /** Currently selected player units. */
  readonly selected = new Set<Unit>();

  private units: Unit[] = [];

  constructor(
    private readonly camera: Camera,
    private readonly map: TileMap,
  ) {}

  /** Supply the live unit array (pass a reference, not a copy). */
  setUnits(units: Unit[]): void {
    this.units = units;
  }

  /**
   * Single-click select at a screen position.
   * additive = true (Shift held) adds to the current selection.
   * Returns true when the selection changed.
   */
  selectAt(screenX: number, screenY: number, additive: boolean): boolean {
    const world = this.camera.screenToWorld(screenX, screenY);
    const hit   = this.hitTest(world);

    if (!additive) this.clearSelection();

    if (hit) {
      hit.selected = true;
      this.selected.add(hit);
      return true;
    }
    return !additive; // cleared = changed
  }

  /**
   * Box-select: select all alive player units whose world position falls
   * inside the rectangle defined by two screen-space corners.
   */
  boxSelect(screenStart: WorldPosition, screenEnd: WorldPosition): boolean {
    const a = this.camera.screenToWorld(screenStart.x, screenStart.y);
    const b = this.camera.screenToWorld(screenEnd.x,   screenEnd.y);

    const minX = Math.min(a.x, b.x);
    const maxX = Math.max(a.x, b.x);
    const minY = Math.min(a.y, b.y);
    const maxY = Math.max(a.y, b.y);

    this.clearSelection();

    for (const unit of this.units) {
      if (!unit.alive || unit.faction !== Faction.Player) continue;
      const wp = this.map.tileTopCenter(unit.gridPos.col, unit.gridPos.row);
      if (wp.x >= minX && wp.x <= maxX && wp.y >= minY && wp.y <= maxY) {
        unit.selected = true;
        this.selected.add(unit);
      }
    }
    return true;
  }

  /** Returns true if a player unit is within hit-radius of the screen position. */
  hasPlayerUnitAt(screenX: number, screenY: number): boolean {
    const world = this.camera.screenToWorld(screenX, screenY);
    return this.hitTest(world) !== null;
  }

  /** Deselect everything. */
  clearSelection(): void {
    for (const u of this.selected) u.selected = false;
    this.selected.clear();
  }

  // ── Hit testing ──────────────────────────────────────────────────────────

  /** Returns the closest alive unit within UNIT_HIT_RADIUS of the world point. */
  private hitTest(world: WorldPosition): Unit | null {
    let closest: Unit | null = null;
    let closestDist = UNIT_HIT_RADIUS;

    for (const unit of this.units) {
      // Only player units are selectable; enemies are auto-targeted by CombatSystem
      if (!unit.alive || unit.faction !== Faction.Player) continue;

      // Test against three positions so clicking the visual unit always registers:
      //   1. gridPos     — logical tile (where gameplay thinks the unit is)
      //   2. path[0]     — next tile target (end of the lerp)
      //   3. lerped pos  — actual rendered centre when moveT is between 0 and 1
      const gridCenter = this.map.tileTopCenter(unit.gridPos.col, unit.gridPos.row);
      const positions  = [gridCenter];
      if (unit.path.length > 0) {
        const nextCenter = this.map.tileTopCenter(unit.path[0].col, unit.path[0].row);
        positions.push(nextCenter);
        if (unit.moveT > 0) {
          positions.push({
            x: gridCenter.x + (nextCenter.x - gridCenter.x) * unit.moveT,
            y: gridCenter.y + (nextCenter.y - gridCenter.y) * unit.moveT,
          });
        }
      }

      for (const wp of positions) {
        const dist = Math.hypot(world.x - wp.x, world.y - wp.y);
        if (dist < closestDist) {
          closest     = unit;
          closestDist = dist;
        }
      }
    }
    return closest;
  }
}
